import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F4F5FE]">
      {/* Header */}
      <header className="bg-[#0A0A12] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo-fascinai.png" alt="FASCINAI" className="h-8 w-auto" />
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-[#1A1C2E] mb-2">Términos de Servicio</h1>
          <p className="text-gray-500 mb-8">Última actualización: 8 de marzo de 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-gray-600">
            
            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">1. Aceptación de Términos</h2>
            <p>
              Al acceder y utilizar FASCINAI ONE ("el Servicio"), aceptas estar sujeto a estos Términos de Servicio. Si no estás de acuerdo con alguna parte de estos términos, no debes usar el Servicio.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">2. Descripción del Servicio</h2>
            <p>
              FASCINAI ONE es una plataforma SaaS (Software as a Service) de generación de marketing automatizado con IA, diseñada para emprendedores, infoproductores y agencias que necesitan crear funnels completos de forma rápida.
            </p>
            <p>El Servicio incluye:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Generación de landing pages con IA</li>
              <li>Creación de secuencias de email y WhatsApp</li>
              <li>Generación de copies para anuncios</li>
              <li>Creación de creativos con IA (imágenes y vídeo)</li>
              <li>Integración con plataformas de marketing (GoHighLevel, Meta Ads, Google Ads)</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">3. Cuentas de Usuario</h2>
            
            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">3.1 Requisitos para Registro</h3>
            <p>Para utilizar el Servicio, debes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Ser mayor de 18 años</li>
              <li>Proporcionar información veraz y completa</li>
              <li>Mantener actualizada tu información de cuenta</li>
              <li>Mantener la seguridad de tu contraseña</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">3.2 Responsabilidades del Usuario</h3>
            <p>Eres responsable de:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Toda actividad que ocurra bajo tu cuenta</li>
              <li>Notificar inmediatamente cualquier uso no autorizado</li>
              <li>Cumplir con todas las leyes aplicables</li>
              <li>No compartir credenciales de acceso</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">4. Suscripciones y Pagos</h2>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">4.1 Planes Disponibles</h3>
            <p>Ofrecemos los siguientes planes de suscripción:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Plan Creador (39€/mes):</strong> 500 créditos/mes, hasta 5 proyectos, wizard completo, todas las integraciones</li>
              <li><strong>Plan Agencia (119€/mes):</strong> 2.000 créditos/mes, proyectos ilimitados, white label, multi-usuario (5 usuarios), API access</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">4.2 Facturación</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Los pagos se procesan mensualmente según tu elección</li>
              <li>Todos los pagos se procesan a través de Stripe</li>
              <li>Los precios están en Euros (€) e incluyen IVA cuando aplique</li>
              <li>La facturación se realiza automáticamente al inicio de cada período</li>
              <li>Recibirás una factura por email después de cada pago exitoso</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">4.3 Política de Reembolsos</h3>
            <p>No ofrecemos reembolsos una vez completado el período de prueba y realizado el primer pago. Puedes cancelar tu suscripción en cualquier momento para evitar cargos futuros.</p>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">4.4 Upgrades y Downgrades</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Upgrade:</strong> Se aplica inmediatamente. Se cobra únicamente la diferencia prorrateada por los días restantes del período de facturación actual.</li>
              <li><strong>Downgrade:</strong> Se programa para aplicarse al final del período de facturación actual. Mantienes tu plan actual hasta esa fecha.</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">4.5 Cancelación</h3>
            <p>Puedes cancelar tu suscripción en cualquier momento desde Configuración → Facturación. La cancelación será efectiva al final del período de facturación pagado. No se ofrecen reembolsos prorrateados por cancelaciones.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">5. Uso Aceptable</h2>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">5.1 Prohibiciones</h3>
            <p>No está permitido:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Revender o redistribuir el Servicio sin autorización expresa</li>
              <li>Realizar scraping o extracción masiva de datos de la plataforma</li>
              <li>Intentar acceder a sistemas o datos no autorizados</li>
              <li>Usar el Servicio para actividades ilegales o fraudulentas</li>
              <li>Sobrecargar o interferir con la infraestructura del Servicio</li>
              <li>Crear cuentas falsas o automatizadas</li>
              <li>Compartir accesos de cuenta entre múltiples usuarios</li>
              <li>Realizar ingeniería inversa del software</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">5.2 Límites por Plan</h3>
            <p>Cada plan tiene límites específicos de créditos y proyectos que deben respetarse. El uso excesivo o abusivo que afecte el rendimiento del Servicio puede resultar en la suspensión temporal o permanente de la cuenta.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">6. Propiedad Intelectual</h2>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">6.1 Propiedad de FASCINAI ONE</h3>
            <p>
              Todo el contenido, características y funcionalidad del Servicio (incluyendo pero no limitado a texto, gráficos, logos, iconos, imágenes, software, código fuente y compilaciones de datos) son propiedad exclusiva de FASCINADS SOLUCIONES SL y están protegidos por leyes de propiedad intelectual españolas e internacionales.
            </p>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">6.2 Tus Datos</h3>
            <p>Retienes todos los derechos de propiedad sobre los datos que subes o generas a través del Servicio. FASCINAI ONE no reclama ningún derecho de propiedad sobre tus datos.</p>
            <p>Al usar el Servicio, nos otorgas una licencia limitada, no exclusiva y revocable para usar, almacenar y procesar tus datos únicamente con el propósito de proporcionar el Servicio.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">7. Privacidad y Seguridad de Datos</h2>
            <p>El uso y tratamiento de tus datos personales está regido por nuestra Política de Privacidad.</p>
            <p>Implementamos medidas de seguridad razonables, incluyendo:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Cifrado SSL/TLS para todas las transmisiones de datos</li>
              <li>Almacenamiento seguro en servidores protegidos</li>
              <li>Autenticación segura con bcrypt para contraseñas</li>
              <li>Copias de seguridad regulares</li>
              <li>Monitoreo de seguridad continuo</li>
            </ul>
            <p>Sin embargo, ningún método de transmisión por Internet es 100% seguro. No podemos garantizar la seguridad absoluta de tus datos.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">8. Limitación de Responsabilidad</h2>
            <p>El Servicio se proporciona "tal cual" y "según disponibilidad" sin garantías de ningún tipo, expresas o implícitas.</p>
            <p>FASCINADS SOLUCIONES SL no garantiza que:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>El Servicio funcionará de manera ininterrumpida o sin errores</li>
              <li>Los defectos serán corregidos inmediatamente</li>
              <li>El Servicio estará libre de virus u otros componentes dañinos</li>
              <li>Los resultados obtenidos serán siempre precisos o fiables</li>
              <li>Las integraciones con servicios de terceros funcionarán sin interrupciones</li>
            </ul>
            <p>La responsabilidad máxima de FASCINADS SOLUCIONES SL estará limitada al monto pagado por el usuario en los últimos 12 meses.</p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">9. Disponibilidad del Servicio</h2>
            <p>Nos esforzamos por mantener el Servicio disponible 24/7, pero no garantizamos disponibilidad del 100%.</p>
            <p>Nos reservamos el derecho de:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Realizar mantenimiento programado (notificando con antelación razonable)</li>
              <li>Suspender el Servicio temporalmente por razones técnicas o de seguridad</li>
              <li>Modificar, agregar o discontinuar características con previo aviso</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">10. Modificaciones del Servicio y Precios</h2>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">10.1 Modificaciones del Servicio</h3>
            <p>FASCINADS SOLUCIONES SL se reserva el derecho de modificar, agregar o discontinuar características del Servicio. Los cambios significativos serán notificados con al menos 30 días de antelación.</p>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">10.2 Cambios de Precios</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Los precios pueden cambiar con 30 días de aviso para nuevos clientes</li>
              <li>Los clientes existentes mantendrán su precio durante el período de facturación actual</li>
              <li>Tienes derecho a cancelar antes de que se aplique el nuevo precio</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">11. Terminación de Cuenta</h2>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">11.1 Terminación por el Usuario</h3>
            <p>Puedes cancelar tu cuenta en cualquier momento desde Configuración → Facturación → Cancelar suscripción.</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Tus datos se conservarán durante 30 días para permitir recuperación</li>
              <li>Después de 30 días, tus datos serán eliminados permanentemente</li>
              <li>Puedes solicitar eliminación inmediata contactando a soporte</li>
            </ul>

            <h3 className="text-lg font-semibold text-[#1A1C2E] mt-6 mb-3">11.2 Terminación por FASCINAI ONE</h3>
            <p>Podemos suspender o terminar tu cuenta inmediatamente si:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violas estos Términos de Servicio</li>
              <li>Tu pago es rechazado repetidamente</li>
              <li>Usas el Servicio de manera fraudulenta, abusiva o ilegal</li>
              <li>Lo exige la ley o una orden judicial</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">12. Integraciones de Terceros</h2>
            <p>El Servicio permite conectar con plataformas de terceros (GoHighLevel, Meta Ads, Google Ads, etc.).</p>
            <p>Reconoces que:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Estas integraciones dependen de las APIs de terceros</li>
              <li>FASCINAI ONE no controla estas plataformas ni sus políticas</li>
              <li>Las integraciones pueden dejar de funcionar si el tercero cambia su API</li>
              <li>Debes cumplir con los términos de servicio de cada plataforma</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">13. Indemnización</h2>
            <p>
              Aceptas indemnizar, defender y eximir de responsabilidad a FASCINADS SOLUCIONES SL, sus directores, empleados, agentes y afiliados de cualquier reclamo, daño, obligación, pérdida, responsabilidad, costo o deuda que surjan de tu uso o mal uso del Servicio, violación de estos Términos, o violación de derechos de terceros.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">14. Ley Aplicable y Jurisdicción</h2>
            <p>
              Estos Términos se regirán e interpretarán de acuerdo con las leyes de España, sin dar efecto a ningún principio de conflictos de leyes.
            </p>
            <p>
              Cualquier disputa relacionada con estos Términos o el uso del Servicio se someterá a la jurisdicción exclusiva de los tribunales de Alicante, España.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">15. Divisibilidad</h2>
            <p>
              Si alguna disposición de estos Términos se considera inválida, ilegal o inaplicable por un tribunal competente, dicha disposición se eliminará o limitará en la medida mínima necesaria, y las disposiciones restantes continuarán en pleno vigor y efecto.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">16. Acuerdo Completo</h2>
            <p>
              Estos Términos de Servicio, junto con nuestra Política de Privacidad y Política de Cookies, constituyen el acuerdo completo entre tú y FASCINADS SOLUCIONES SL respecto al uso del Servicio.
            </p>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">17. Contacto y Soporte</h2>
            <p>Para preguntas sobre estos Términos de Servicio, soporte técnico o cualquier otra consulta, contáctanos en:</p>
            <ul className="list-none space-y-2">
              <li><strong>FASCINADS SOLUCIONES SL</strong></li>
              <li><strong>NIF:</strong> B70880182</li>
              <li><strong>Dirección:</strong> Juez José Bernabeu Rocamora, 31 - 03348 Granja de Rocamora (Alicante), España</li>
              <li><strong>Email de soporte:</strong> soporte@fascinai.pro</li>
              <li><strong>Horario de soporte:</strong> Lunes a Viernes, 9:00 - 18:00 CET</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1A1C2E] mt-10 mb-4">18. Reconocimiento</h2>
            <p>Al registrarte y usar FASCINAI ONE, reconoces que:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Has leído y entendido estos Términos de Servicio en su totalidad</li>
              <li>Aceptas estar legalmente vinculado por estos términos</li>
              <li>Tienes la capacidad legal para aceptar estos términos</li>
              <li>Si actúas en nombre de una empresa, tienes autoridad para vincularla</li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A12] py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-white/30">© 2026 Fascinads Soluciones SL</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
